import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH20_QUESTIONS } from "../data/neetPhysicsCh20Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 20: Magnetism and Matter (NCERT Class 12 Physics).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Bar Magnets & Field Lines", "Earth's Magnetism", "Magnetization & Magnetic Materials", "Permanent Magnets & Electromagnets"];

const SECTION_COLORS = {
  "Bar Magnets & Field Lines":              "#2563eb",
  "Earth's Magnetism":                      "#0891b2",
  "Magnetization & Magnetic Materials":     "#7c3aed",
  "Permanent Magnets & Electromagnets":     "#ea580c",
};

const CONCEPTS = [
  // ── Bar Magnets & Field Lines ───────────────────────────────────────────────
  { section: "Bar Magnets & Field Lines", title: "Bar magnet as an equivalent solenoid",
    explanation: "A current-carrying solenoid produces a magnetic field pattern -- with a north pole at one end and a south pole at the other -- that is identical to the field of a bar magnet of the same size. This equivalence lets a bar magnet be modeled as an ideal magnetic dipole made of many small current loops (atomic current loops) stacked along its length, each contributing to a net magnetic moment m = NIA for a solenoid of N turns, current I, and cross-sectional area A.",
    example: `Solenoid magnetic moment:      m = N I A
(N = turns, I = current, A = cross-sectional area)

A bar magnet is the "solid" analogue of a solenoid --
outside the two, the field lines are indistinguishable.` },
  { section: "Bar Magnets & Field Lines", title: "Magnetic field lines and their properties",
    explanation: "Magnetic field lines are imaginary curves whose tangent at any point gives the direction of B there, and whose relative spacing/density indicates the field's relative strength (crowded lines = strong field). Unlike electric field lines, magnetic field lines always form CLOSED continuous loops -- outside a magnet they run from the N pole to the S pole, and inside the magnet's material they continue from the S pole back to the N pole. Two field lines can never intersect, since that would mean two different field directions at the same point.",
    example: `Key properties:
1. Tangent at a point       -> direction of B
2. Crowded lines             -> stronger field
3. Form CLOSED loops (N->S outside, S->N inside)
4. Never intersect each other` },
  { section: "Bar Magnets & Field Lines", title: "Magnetic dipole moment of a bar magnet",
    explanation: "A bar magnet behaves like a magnetic dipole with two poles of pole strength qm, separated by an effective length 2l (the distance between poles), directed from the S pole to the N pole. Its magnetic dipole moment is m = qm(2l), a vector quantity with SI unit A.m2 (equivalently J/T). A current loop of area A carrying current I has magnetic moment m = IA, and both kinds of dipole moment produce identical dipole-shaped magnetic fields far from the source.",
    example: `Bar magnet:     m = qm . (2l)      [qm = pole strength, 2l = length]
Current loop:   m = I . A          [I = current, A = loop area]

Unit of magnetic moment: A.m^2  (also written J/T)
Direction: from S pole to N pole (along the magnet's axis)` },
  { section: "Bar Magnets & Field Lines", title: "Magnetic field due to a bar magnet: axial and equatorial points",
    explanation: "For a short bar magnet of moment m, the field at a point on the AXIAL line (on the magnet's axis, distance r from its centre, r >> length) is B_axial = (mu0/4pi) x (2m/r^3), directed along m. The field at a point on the EQUATORIAL line (on the perpendicular bisector, same distance r) is B_eq = (mu0/4pi) x (m/r^3), directed opposite (antiparallel) to m. At equal distances, the axial field is exactly TWICE the equatorial field.",
    example: `NCERT-style example: m = 0.48 J/T (A.m^2), r = 0.10 m, mu0/4pi = 1e-7

Axial:      B_axial = 1e-7 x (2 x 0.48)/(0.1)^3 = 1e-7 x 960 = 9.6e-5 T
Equatorial: B_eq     = 1e-7 x (0.48)/(0.1)^3     = 1e-7 x 480 = 4.8e-5 T

Check: B_axial = 2 x B_eq   (9.6e-5 = 2 x 4.8e-5)  -- confirmed` },
  { section: "Bar Magnets & Field Lines", title: "Torque and potential energy of a bar magnet in a uniform field",
    explanation: "When a bar magnet of moment m is placed in a uniform magnetic field B at angle theta to the field, it experiences a torque tau = m B sin(theta) (vector: tau = m x B) that tends to align m with B. Its potential energy is U(theta) = -m B cos(theta), taking U=0 when m is perpendicular to B. U is minimum (most stable) when theta=0 (m parallel to B) and maximum (most unstable) when theta=180 degrees (m antiparallel to B).",
    example: `NCERT-style example: torque tau = 0.016 N.m at theta=30 deg, B=0.08 T
tau = mB sin(theta)  ->  m = tau /(B sin(theta)) = 0.016/(0.08 x 0.5) = 0.4 A.m^2

Work done rotating from stable (0 deg) to unstable (180 deg):
W = U(180) - U(0) = [-mBcos180] - [-mBcos0] = mB + mB = 2mB
W = 2 x 0.4 x 0.08 = 0.064 J` },

  // ── Earth's Magnetism ────────────────────────────────────────────────────────
  { section: "Earth's Magnetism", title: "Magnetic declination",
    explanation: "At a place, the geographic meridian (vertical plane through true geographic N-S) and the magnetic meridian (vertical plane containing the direction a freely suspended magnetic needle points) generally do not coincide. The angle between them, at that place, is called the magnetic DECLINATION. A compass needle actually points along the local magnetic meridian, so it points true geographic north only where the declination happens to be zero.",
    example: `Declination = angle between:
  Geographic meridian (true N-S)
       and
  Magnetic meridian (compass-needle N-S)

In India, declination is small (within a few degrees) at most
places, so a compass reading is close to, but not exactly, true north.` },
  { section: "Earth's Magnetism", title: "Angle of dip (inclination)",
    explanation: "The Earth's total magnetic field B at a place is not horizontal in general -- it makes an angle with the horizontal called the angle of DIP (or inclination), delta. Dip is measured in the vertical plane containing the magnetic meridian. At the magnetic equator, dip = 0 degrees (field is fully horizontal); at the magnetic poles, dip = 90 degrees (field is fully vertical, pointing straight down/up).",
    example: `Angle of dip delta:
  Magnetic equator -> delta = 0 deg    (B purely horizontal)
  Magnetic poles   -> delta = 90 deg   (B purely vertical)
  Mid latitudes    -> 0 < delta < 90 deg` },
  { section: "Earth's Magnetism", title: "Horizontal and vertical components of Earth's field",
    explanation: "The Earth's total field B at a place resolves into a horizontal component H (along the magnetic meridian) and a vertical component Z, related to the dip angle delta by H = B cos(delta) and Z = B sin(delta), so tan(delta) = Z/H and B = sqrt(H^2 + Z^2). H, together with declination and dip, are together called the three ELEMENTS of Earth's magnetic field, and fully specify B's magnitude and direction at that place.",
    example: `H = B cos(delta),   Z = B sin(delta),   tan(delta) = Z/H

Example: H = 0.36 G, delta = 60 deg
Z = H tan(delta) = 0.36 x tan60 deg = 0.36 x 1.732 = 0.62 G
B = H / cos(delta) = 0.36 / 0.5 = 0.72 G` },
  { section: "Earth's Magnetism", title: "The Earth as a magnetic dipole",
    explanation: "To a good approximation, the Earth behaves as if a huge bar magnet (magnetic dipole of moment ~8x10^22 A.m^2) were buried at its centre, with its magnetic axis tilted about 11.3 degrees from the Earth's geographic (rotation) axis -- this tilt is why magnetic and geographic poles don't coincide, and why declination varies from place to place. The Earth's magnetic SOUTH pole lies close to the geographic NORTH pole (and vice versa) -- which is precisely why the N pole of a compass needle, attracted toward that nearby magnetic south pole, points geographically northward.",
    example: `Earth's magnetic moment  ~ 8 x 10^22 A.m^2
Tilt of magnetic axis from geographic axis  ~ 11.3 degrees

Earth's magnetic SOUTH pole  -- near geographic NORTH pole
Earth's magnetic NORTH pole  -- near geographic SOUTH pole
(this is why a compass N-pole is pulled to point geographic north)` },
  { section: "Earth's Magnetism", title: "Neutral points and the tangent law",
    explanation: "When a small bar magnet is placed near a compass in the presence of Earth's horizontal field H, there exist NEUTRAL POINTS where the magnet's own field exactly cancels H, making the net field zero (a compass here shows no preferred direction). The TANGENT LAW states that if a compass needle sits under two mutually perpendicular fields -- a known field B and Earth's H -- it settles at angle theta to H where B = H tan(theta) -- this is the working principle of a tangent galvanometer and of magnetometer experiments used to find a magnet's moment m or Earth's field H.",
    example: `Tangent law:  B = H tan(theta)

Neutral point on AXIAL line (magnet's N pole toward geographic south):
  mu0/4pi x (2m/d^3) = H

Neutral point on EQUATORIAL line (magnet's N pole toward geographic north):
  mu0/4pi x (m/d^3) = H` },

  // ── Magnetization & Magnetic Materials ────────────────────────────────────────
  { section: "Magnetization & Magnetic Materials", title: "Magnetization (M) and magnetic intensity (H)",
    explanation: "MAGNETIZATION M of a material is defined as its net magnetic moment per unit volume (M = m_net/V), SI unit A/m -- it measures how strongly the material's internal atomic dipoles have aligned. The MAGNETIC INTENSITY (or magnetizing field) H is the external field applied by, say, a solenoid's current (in the absence of any material core), also with SI unit A/m; for a solenoid with n turns per unit length carrying current I, H = nI.",
    example: `Magnetization:    M = (net magnetic moment) / (volume)     [A/m]
Magnetic intensity (solenoid): H = n I                       [A/m]
  (n = turns per unit length, I = current)

Both M and H share the same SI unit, A/m, unlike B (in tesla).` },
  { section: "Magnetization & Magnetic Materials", title: "Magnetic susceptibility",
    explanation: "Magnetic susceptibility chi measures how easily a material magnetizes in response to an applied field: chi = M/H, a dimensionless ratio. Diamagnetic materials have small NEGATIVE chi (weakly oppose the field), paramagnetic materials have small POSITIVE chi (weakly align with the field), and ferromagnetic materials have very LARGE positive chi (of order hundreds to a hundred-thousand).",
    example: `chi = M / H     (dimensionless)

Diamagnetic:   chi small and NEGATIVE   (~ -10^-5)
Paramagnetic:  chi small and POSITIVE   (~ +10^-3 to +10^-5)
Ferromagnetic: chi LARGE and POSITIVE   (~ 10^2 to 10^5)` },
  { section: "Magnetization & Magnetic Materials", title: "Permeability and relative permeability",
    explanation: "The absolute (magnetic) permeability mu of a material relates B and H via B = mu H; mu0 is the permeability of free space (vacuum). The RELATIVE permeability mu_r = mu/mu0 = 1 + chi tells us how many times more (or less) effective the material is at supporting magnetic field lines compared to vacuum. Diamagnetic materials have mu_r slightly less than 1; paramagnetic materials have mu_r slightly more than 1; ferromagnetic materials have mu_r much greater than 1 (hundreds to thousands).",
    example: `mu_r = mu / mu0 = 1 + chi

Diamagnetic:   mu_r slightly < 1   (e.g. ~0.99999)
Paramagnetic:  mu_r slightly > 1   (e.g. ~1.0001)
Ferromagnetic: mu_r >> 1           (e.g. ~1000s for soft iron)` },
  { section: "Magnetization & Magnetic Materials", title: "The relation B = μ0(H + M)",
    explanation: "Inside a magnetized material placed in an external magnetizing field H, the total magnetic field B has two contributions: the field due to the free current/external source (mu0 H) and the field due to the material's own induced magnetization (mu0 M). Combining, B = mu0(H + M). Using M = chi H, this becomes B = mu0(1+chi)H = mu0 mu_r H = mu H -- linking magnetization, susceptibility, and permeability into one consistent picture.",
    example: `B = mu0 (H + M)              <- fundamental relation
M = chi H                     <- definition of susceptibility
=>  B = mu0 (1 + chi) H = mu0 . mu_r . H = mu H

So for a paramagnetic/diamagnetic material with small chi,
B is only slightly different from the vacuum value mu0 H.` },
  { section: "Magnetization & Magnetic Materials", title: "Curie's law and the Curie temperature",
    explanation: "For paramagnetic materials, susceptibility falls as temperature rises, since increasing thermal agitation disrupts the alignment of atomic dipoles with the field -- this is CURIE'S LAW, chi = C/T (C = Curie constant, T in kelvin). Ferromagnetic materials retain their strong spontaneous magnetization only below a critical CURIE TEMPERATURE T_c; above T_c, thermal agitation destroys the domain alignment and the material becomes merely paramagnetic (e.g. iron's Curie temperature is about 1043 K).",
    example: `Curie's law (paramagnetic):   chi = C / T     (C = Curie constant)

Above Curie temperature Tc: ferromagnetic -> paramagnetic
  Iron:   Tc ~ 1043 K  (770 deg C)

As T increases, chi decreases (weaker magnetic response)` },

  // ── Permanent Magnets & Electromagnets ────────────────────────────────────────
  { section: "Permanent Magnets & Electromagnets", title: "Diamagnetic substances",
    explanation: "Diamagnetic substances develop an induced magnetization OPPOSITE to an applied external field, so they are feebly REPELLED by a magnet and tend to move from stronger to weaker field regions. Their susceptibility chi is small and negative, essentially independent of temperature. This behaviour arises in materials whose atoms have no net (unpaired) magnetic moment on their own. Examples: bismuth, copper, water, lead, silver, and gold (superconductors are perfect diamagnets, chi = -1).",
    example: `Diamagnetism:
  chi : small, NEGATIVE, ~temperature independent
  Behaviour : weakly REPELLED by a magnet
  Examples  : bismuth, copper, water, lead, silver, gold

  A diamagnetic rod placed in a non-uniform field tends to
  move toward the WEAKER field region.` },
  { section: "Permanent Magnets & Electromagnets", title: "Paramagnetic substances",
    explanation: "Paramagnetic substances have atoms/ions with a net (unpaired-electron) magnetic moment that aligns weakly along an applied field, so the material is feebly ATTRACTED toward stronger field regions. Susceptibility chi is small and positive, and decreases with rising temperature (Curie's law), since thermal motion disrupts the weak alignment. Examples: aluminium, sodium, calcium, oxygen (at STP), platinum, copper chloride.",
    example: `Paramagnetism:
  chi : small, POSITIVE, decreases with temperature (Curie's law)
  Behaviour : weakly ATTRACTED toward stronger field
  Examples  : aluminium, sodium, calcium, platinum, oxygen (gas)

  A paramagnetic rod placed in a non-uniform field tends to
  move toward the STRONGER field region.` },
  { section: "Permanent Magnets & Electromagnets", title: "Ferromagnetic substances and magnetic domains",
    explanation: "Ferromagnetic substances have atoms with strong mutual (exchange) interaction that spontaneously aligns large groups of neighbouring atomic dipoles into DOMAINS, each fully magnetized even without an external field. In an unmagnetized sample the domains point in random directions and cancel out overall; applying an external field grows/aligns the domains, producing a very large net magnetization -- so chi is large and positive (10^2-10^5), and the material is strongly ATTRACTED to a magnet. Examples: iron, cobalt, nickel, gadolinium, and their alloys.",
    example: `Ferromagnetism:
  chi : LARGE and POSITIVE (10^2 - 10^5)
  Behaviour : strongly ATTRACTED to a magnet
  Mechanism : spontaneously magnetized DOMAINS align with field
  Examples  : iron, cobalt, nickel, gadolinium, alnico alloys

  Above the Curie temperature, domains are destroyed by thermal
  agitation and the material becomes simply paramagnetic.` },
  { section: "Permanent Magnets & Electromagnets", title: "Hysteresis in ferromagnetic materials",
    explanation: "When a ferromagnetic sample is taken through a full cycle of magnetizing field H (increase, decrease, reverse, and back), the resulting magnetization B LAGS BEHIND H, tracing a loop instead of retracing the same curve -- this lag is called HYSTERESIS. Key points on the B-H loop: RETENTIVITY (or remanence) is the residual B left when H is reduced to zero; COERCIVITY is the reverse H needed to bring B down to zero. The area enclosed by the loop represents energy dissipated as heat per unit volume, per cycle, within the material.",
    example: `B-H Hysteresis loop -- key points:
  Retentivity (B_r) : residual B when H = 0
  Coercivity  (H_c) : reverse H needed to make B = 0
  Loop area         : energy lost as heat per cycle (per unit volume)

  Soft iron    -> thin/tall loop (low loss, easily (de)magnetized)
                  -> good for electromagnets/transformers
  Steel/alnico -> fat loop (high retentivity & coercivity)
                  -> good for permanent magnets` },
  { section: "Permanent Magnets & Electromagnets", title: "Permanent magnets and electromagnets — material choice",
    explanation: "A good PERMANENT MAGNET material must retain strong magnetization after the external field is removed and resist becoming demagnetized by stray fields or knocks -- i.e. it needs HIGH retentivity and HIGH coercivity (e.g. steel, alnico -- an aluminium-nickel-cobalt-iron alloy). A good ELECTROMAGNET core, by contrast, must magnetize and demagnetize easily and completely as the current is switched on/off -- i.e. it needs HIGH permeability with LOW retentivity and LOW coercivity (soft iron is ideal), minimizing hysteresis energy loss in devices switched repeatedly (transformers, electric bells, cranes).",
    example: `Permanent magnet material:  HIGH retentivity + HIGH coercivity
   Examples: steel, alnico (Al-Ni-Co-Fe alloy)

Electromagnet core material: HIGH permeability, LOW retentivity
                              and LOW coercivity
   Example: soft iron

  (soft iron magnetizes strongly under current but loses nearly
   all magnetization the instant the current is switched off)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh20Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh20Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh20Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH20_QUESTIONS : NEET_PHYSICS_CH20_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 20</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Magnetism and Matter</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12, Ch. 5 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH20_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH20_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
